import { useId, useState, type DragEvent, type ReactNode } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  DotsSixVerticalIcon,
  XIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import {
  WORKBOOK_PIVOT_DATE_GROUPS,
  WORKBOOK_PIVOT_SHOW_AS,
  WORKBOOK_PIVOT_SUMMARIES,
  type WorkbookFilter,
  type WorkbookPivotGroup,
  type WorkbookPivotSpec,
  type WorkbookPivotValue,
} from '@erp/shared-types';
import { pivotValueName } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SampleValuesNote } from '../WorksheetValueListFilter';
import type { PivotArea } from './pivotEdits';
import type { PivotField } from './pivotFields';
import { usePivotLabels } from './usePivotLabels';

/** A pivot card's compact select trigger. */
export const SELECT_TRIGGER_CLASS = 'h-7 min-w-0 flex-1 text-xs';

/** The Group by picker's value for no date grouping. */
const EACH_VALUE = 'each-value';

/** What a card's header needs: its field, its place, and the moves it offers. */
export type CardFrame = {
  area: PivotArea;
  index: number;
  count: number;
  field: string;
  readOnly: boolean;
  onRemove: () => void;
  onMove: (delta: -1 | 1) => void;
  onDragStart: (event: DragEvent) => void;
};

/** A field card: its drag handle, name, and reorder and remove buttons, over its settings. */
function FieldCard({
  frame,
  children,
}: {
  frame: CardFrame;
  children?: ReactNode;
}) {
  const gt = useGT();
  const labels = usePivotLabels();
  return (
    <li
      className="space-y-1.5 rounded-md border bg-background p-2"
      draggable={!frame.readOnly}
      onDragStart={frame.onDragStart}
      aria-label={gt('{field} in {area}', {
        context: 'pivot field card',
        field: frame.field,
        area: labels.areas[frame.area],
      })}
    >
      <div className="flex items-center gap-1">
        {!frame.readOnly && (
          <DotsSixVerticalIcon
            className="size-3.5 shrink-0 cursor-grab text-muted-foreground"
            aria-hidden
          />
        )}
        <span className="mr-auto truncate text-xs font-medium">
          {frame.field}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          disabled={frame.readOnly || frame.index === 0}
          aria-label={gt('Move {field} up', {
            context: 'pivot field card',
            field: frame.field,
          })}
          onClick={() => frame.onMove(-1)}
        >
          <ArrowUpIcon className="size-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          disabled={frame.readOnly || frame.index === frame.count - 1}
          aria-label={gt('Move {field} down', {
            context: 'pivot field card',
            field: frame.field,
          })}
          onClick={() => frame.onMove(1)}
        >
          <ArrowDownIcon className="size-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          disabled={frame.readOnly}
          aria-label={gt('Remove {field}', {
            context: 'pivot field card',
            field: frame.field,
          })}
          onClick={frame.onRemove}
        >
          <XIcon className="size-3" />
        </Button>
      </div>
      {children}
    </li>
  );
}

/** A labelled setting row; the control names itself by the label's id. */
function Setting({
  label,
  children,
}: {
  label: ReactNode;
  children: (labelId: string) => ReactNode;
}) {
  const labelId = useId();
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span id={labelId} className="w-16 shrink-0">
        {label}
      </span>
      {children(labelId)}
    </div>
  );
}

/** A Row or Column field: its order, what it sorts by, its date grouping and subtotals. */
export function GroupCard({
  frame,
  group,
  spec,
  field,
  innermost,
  change,
}: {
  frame: CardFrame;
  group: WorkbookPivotGroup;
  spec: WorkbookPivotSpec;
  field?: PivotField;
  /** The last Row field, whose subtotals would repeat its rows. */
  innermost: boolean;
  change: (group: WorkbookPivotGroup) => void;
}) {
  const gt = useGT();
  const labels = usePivotLabels();
  const sortValue =
    group.sortBy.by === 'value' ? String(group.sortBy.value) : 'label';
  return (
    <FieldCard frame={frame}>
      <Setting label={<T context="pivot group order">Order</T>}>
        {(labelId) => (
          <Select
            disabled={frame.readOnly}
            value={group.sortBy.direction}
            onValueChange={(direction) =>
              change({
                ...group,
                sortBy: {
                  ...group.sortBy,
                  direction: direction === 'desc' ? 'desc' : 'asc',
                },
              })
            }
          >
            <SelectTrigger
              className={SELECT_TRIGGER_CLASS}
              aria-labelledby={labelId}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">
                {gt('Ascending', { context: 'pivot group order' })}
              </SelectItem>
              <SelectItem value="desc">
                {gt('Descending', { context: 'pivot group order' })}
              </SelectItem>
            </SelectContent>
          </Select>
        )}
      </Setting>
      <Setting label={<T context="pivot group sort by">Sort by</T>}>
        {(labelId) => (
          <Select
            disabled={frame.readOnly}
            value={sortValue}
            onValueChange={(value) => {
              change({
                ...group,
                sortBy:
                  value === 'label'
                    ? { by: 'label', direction: group.sortBy.direction }
                    : {
                        by: 'value',
                        value: Number(value),
                        direction: group.sortBy.direction,
                      },
              });
            }}
          >
            <SelectTrigger
              className={SELECT_TRIGGER_CLASS}
              aria-labelledby={labelId}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="label">{group.field}</SelectItem>
              {spec.values.map((value, index) => (
                <SelectItem key={index} value={String(index)}>
                  {pivotValueName(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Setting>
      {(field?.type === 'date' || field?.type === 'text') && (
        <Setting label={<T context="pivot date grouping">Group by</T>}>
          {(labelId) => (
            <Select
              disabled={frame.readOnly}
              value={group.dateGroup ?? EACH_VALUE}
              onValueChange={(value) => {
                const next = WORKBOOK_PIVOT_DATE_GROUPS.find(
                  (item) => item === value,
                );
                const { dateGroup: _dropped, ...rest } = group;
                change(next ? { ...rest, dateGroup: next } : rest);
              }}
            >
              <SelectTrigger
                className={SELECT_TRIGGER_CLASS}
                aria-labelledby={labelId}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={EACH_VALUE}>
                  {gt('Each value', { context: 'pivot no date grouping' })}
                </SelectItem>
                {WORKBOOK_PIVOT_DATE_GROUPS.map((item) => (
                  <SelectItem key={item} value={item}>
                    {labels.dateGroups[item]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Setting>
      )}
      {frame.area === 'rows' && !innermost && (
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            disabled={frame.readOnly}
            checked={group.subtotals}
            onCheckedChange={(subtotals) => change({ ...group, subtotals })}
          />
          <T context="pivot group subtotals">Show subtotals</T>
        </label>
      )}
    </FieldCard>
  );
}

/** A Value: how it summarizes its field, how it shows, and its column name. */
export function ValueCard({
  frame,
  value,
  change,
}: {
  frame: CardFrame;
  value: WorkbookPivotValue;
  change: (value: WorkbookPivotValue) => void;
}) {
  const gt = useGT();
  const labels = usePivotLabels();
  const defaultName = pivotValueName({ ...value, name: undefined });
  return (
    <FieldCard frame={frame}>
      <Setting label={<T context="pivot value summarize by">Summarize by</T>}>
        {(labelId) => (
          <Select
            disabled={frame.readOnly}
            value={value.summarize}
            onValueChange={(next) => {
              const summarize = WORKBOOK_PIVOT_SUMMARIES.find(
                (item) => item === next,
              );
              if (summarize) change({ ...value, summarize });
            }}
          >
            <SelectTrigger
              className={SELECT_TRIGGER_CLASS}
              aria-labelledby={labelId}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WORKBOOK_PIVOT_SUMMARIES.map((item) => (
                <SelectItem
                  key={item}
                  value={item}
                  itemText={labels.summaries[item]}
                >
                  {/* The name is itemText; this is the second line under it. */}
                  <span className="text-xs text-muted-foreground">
                    {labels.summaryHints[item]}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Setting>
      <Setting label={<T context="pivot value show as">Show as</T>}>
        {(labelId) => (
          <Select
            disabled={frame.readOnly}
            value={value.showAs}
            onValueChange={(next) => {
              const showAs = WORKBOOK_PIVOT_SHOW_AS.find(
                (item) => item === next,
              );
              if (showAs) change({ ...value, showAs });
            }}
          >
            <SelectTrigger
              className={SELECT_TRIGGER_CLASS}
              aria-labelledby={labelId}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WORKBOOK_PIVOT_SHOW_AS.map((item) => (
                <SelectItem key={item} value={item}>
                  {labels.showAs[item]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Setting>
      <Setting label={<T context="pivot value column name">Name</T>}>
        {() => (
          <ValueNameInput
            value={value}
            placeholder={defaultName}
            readOnly={frame.readOnly}
            label={gt('Column name for {field}', {
              context: 'pivot value column name',
              field: value.field,
            })}
            change={change}
          />
        )}
      </Setting>
    </FieldCard>
  );
}

/** The Value's column name, empty for its default name; saved as it is typed. */
function ValueNameInput({
  value,
  placeholder,
  readOnly,
  label,
  change,
}: {
  value: WorkbookPivotValue;
  placeholder: string;
  readOnly: boolean;
  label: string;
  change: (value: WorkbookPivotValue) => void;
}) {
  const incoming = value.name ?? '';
  const [text, setText] = useState(incoming);
  // Follow a name changed elsewhere (undo, another editor), but keep what is
  // being typed when the change is only its own echo (the trimmed text).
  const [synced, setSynced] = useState(incoming);
  if (incoming !== synced) {
    setSynced(incoming);
    if (text.trim() !== incoming) setText(incoming);
  }
  return (
    <Input
      className="h-7 text-xs"
      value={text}
      placeholder={placeholder}
      disabled={readOnly}
      aria-label={label}
      onChange={(event) => {
        setText(event.target.value);
        const name = event.target.value.trim();
        const { name: _previous, ...rest } = value;
        change(name ? { ...rest, name } : rest);
      }}
    />
  );
}

const CONDITIONS = [
  'is-not-one-of',
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
  'is-null',
  'is-not-null',
  'is-error',
  'is-not-error',
] as const satisfies readonly WorkbookFilter['operator'][];
const TAKES_VALUE: ReadonlySet<string> = new Set([
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
]);

/**
 * A Filter: by values (a checklist of the field's values; unchecking one
 * leaves its rows out), or by a condition.
 */
export function FilterCard({
  frame,
  filter,
  field,
  change,
}: {
  frame: CardFrame;
  filter: WorkbookFilter;
  field?: PivotField;
  change: (filter: WorkbookFilter) => void;
}) {
  const gt = useGT();
  const context = 'pivot filter condition';
  const conditionLabels: Record<(typeof CONDITIONS)[number], string> = {
    'is-not-one-of': gt('Filter by values', { context }),
    eq: gt('Equals', { context }),
    neq: gt('Does not equal', { context }),
    gt: gt('Greater than', { context }),
    gte: gt('Greater than or equal to', { context }),
    lt: gt('Less than', { context }),
    lte: gt('Less than or equal to', { context }),
    contains: gt('Contains', { context }),
    'is-null': gt('Is empty', { context }),
    'is-not-null': gt('Is not empty', { context }),
    'is-error': gt('Is error', { context }),
    'is-not-error': gt('Is not error', { context }),
  };
  const condition =
    CONDITIONS.find((item) => item === filter.operator) ?? 'is-not-one-of';
  return (
    <FieldCard frame={frame}>
      <Select
        disabled={frame.readOnly}
        value={condition}
        onValueChange={(next) => {
          const operator = CONDITIONS.find((item) => item === next);
          if (!operator) return;
          const { values: _values, ...rest } = filter;
          change(
            operator === 'is-not-one-of'
              ? { ...rest, operator, value: '', values: [] }
              : { ...rest, operator },
          );
        }}
      >
        <SelectTrigger
          className="h-7 w-full text-xs"
          aria-label={gt('Condition for {field}', {
            context,
            field: filter.column,
          })}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CONDITIONS.map((item) => (
            <SelectItem key={item} value={item}>
              {conditionLabels[item]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {TAKES_VALUE.has(condition) && (
        <Input
          className="h-7 text-xs"
          value={filter.value}
          disabled={frame.readOnly}
          aria-label={gt('Value for {field}', {
            context,
            field: filter.column,
          })}
          onChange={(event) => change({ ...filter, value: event.target.value })}
        />
      )}
      {condition === 'is-not-one-of' && (
        <ValueChecklist
          filter={filter}
          field={field}
          readOnly={frame.readOnly}
          change={change}
        />
      )}
    </FieldCard>
  );
}

function ValueChecklist({
  filter,
  field,
  readOnly,
  change,
}: {
  filter: WorkbookFilter;
  field?: PivotField;
  readOnly: boolean;
  change: (filter: WorkbookFilter) => void;
}) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  const excluded = new Set(filter.values ?? []);
  const known = field?.values;
  const entries = [
    ...(known?.values.map((entry) => ({
      value: entry.value,
      label: entry.value,
      count: entry.count,
    })) ?? []),
    ...(known?.emptyCount
      ? [
          {
            value: '',
            label: gt('(Empty)', { context: 'pivot filter empty value' }),
            count: known.emptyCount,
          },
        ]
      : []),
  ].filter((entry) => entry.label.toLowerCase().includes(search.toLowerCase()));
  function toggle(value: string) {
    const next = new Set(excluded);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    change({ ...filter, values: [...next] });
  }
  if (!known)
    return (
      <p className="text-xs text-muted-foreground">
        <T context="pivot filter values unknown">
          Run to see this field's values. Showing all items.
        </T>
      </p>
    );
  return (
    <div className="space-y-1">
      <Input
        className="h-7 text-xs"
        value={search}
        placeholder={gt('Search values', {
          context: 'pivot filter values search',
        })}
        aria-label={gt('Search values of {field}', {
          context: 'pivot filter values search',
          field: filter.column,
        })}
        onChange={(event) => setSearch(event.target.value)}
      />
      <ul className="max-h-40 overflow-y-auto text-xs">
        {entries.map((entry) => (
          <li key={entry.value}>
            <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-accent">
              <Checkbox
                disabled={readOnly}
                checked={!excluded.has(entry.value)}
                aria-label={entry.label}
                onCheckedChange={() => toggle(entry.value)}
              />
              <span className="min-w-0 flex-1 truncate">{entry.label}</span>
              <span className="tabular-nums text-muted-foreground">
                {entry.count}
              </span>
            </label>
          </li>
        ))}
      </ul>
      {known.sampleRows !== undefined ? (
        <SampleValuesNote rows={known.sampleRows} />
      ) : (
        known.truncated && (
          <p className="text-xs text-muted-foreground">
            <T context="pivot filter values partial">
              Some values aren't listed.
            </T>
          </p>
        )
      )}
    </div>
  );
}
